export interface BaseResponse<T = any> {
  data: T;
  success: boolean;
  errorCode: string;
  errorMessage: string;
}

export class RequestError extends Error {
  constructor(
    message: string,
    public status: number,
    public statusText: string,
    public response: Response,
  ) {
    super(message);
  }
}

export class BusinessError extends Error {
  constructor(
    message: string,
    public code: string,
    public response: Response,
    public data?: any,
  ) {
    super(message);
  }
}

async function redirectToLogin() {
  const res = await fetch('/home/logout');
  const json = await res.json();
  window.location.href = json?.data ?? '/';
}

function isBaseResponse<T = any>(res: any): res is BaseResponse<T> {
  return typeof res === 'object' && 'success' in res && 'errorCode' in res && 'errorMessage' in res;
}

export async function request<T = any>({
  url,
  method = 'GET',
  data,
  query,
  headers = {},
  downloadFile = false,
}: {
  url: string;
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  data?: any;
  query?: Record<string, string>;
  headers?: Record<string, string>;
  downloadFile?: boolean;
}): Promise<T> {
  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  };

  url = `/api${url}`;

  if (query) {
    const queryString = new URLSearchParams(query).toString();
    url += `?${queryString}`;
  }

  //console.log('data-access: data', data);
  //console.log('data-access: JSON.stringify(data)', JSON.stringify(data));
  //console.log('data-access: JSON.stringify(data)', JSON.stringify(data));
  //console.log('data instanceof File', data instanceof File);

  if (data) {
    config.body = data instanceof File ? data : JSON.stringify(data);
  }

  const response = await fetch(url, config);
  if (!response.ok) {
    if (response.status === 401) {
      void redirectToLogin();
      return undefined as T;
    }
    throw new RequestError('Request failed', response.status, response.statusText, response);
  }

  // Handle file download before content-type check (downloads return non-JSON content types)
  if (downloadFile) {
    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    let filename = 'download';

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"|filename=([^;\s]+)/);
      if (filenameMatch) {
        filename = filenameMatch[1] || filenameMatch[2];
      }
    }

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return { filename, blob } as T;
  }

  // Detect HTML response (login page redirect after session expiry)
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    window.location.href = '/home/logout';
    return undefined as T;
  }

  // Handle JSON response
  const res = await response.json();
  if (!isBaseResponse<T>(res)) {
    throw new RequestError(
      'Invalid response format',
      response.status,
      response.statusText,
      response,
    );
  }

  if (!res.success) {
    throw new BusinessError(res.errorMessage, res.errorCode, response, res.data);
  }

  return res.data as T;
}

export async function getData(endpoint) {
  return await request({ url: endpoint });
}

export async function postData(endpoint, body) {
  return await request({ url: endpoint, data: body, method: 'POST' });
}

export async function patchData(endpoint, body) {
  return await request({ url: endpoint, data: body, method: 'PATCH' });
}

export async function postFile(endpoint, file) {
  return await request({ url: endpoint, data: file, method: 'POST' });
}

export async function deleteData(endpoint) {
  return await request({ url: endpoint, method: 'DELETE' });
}
